import { Box, Center, Group, Stack, Text, Title } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import PageSizeOptions from '../../constants/pageSizeOptions';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import clickTrackingVerified from '../../helpers/clickTrackingVerified';
import { daysAgoISODate, dateToEndOfDay, dateToStartOfDay, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';
import SenderVerificationAlert from '../SenderVerificationAlert';
import EmailSendingHoldAlert from '../EmailSendingHoldAlert';
import ErrorMessage from '../ErrorMessage';
import Metadata from '../Metadata';
import MetricsSelector from '../MetricsSelector';
import NewElementButton from '../NewElementButton';
import PageFilter from '../PageFilter';
import Pager from '../Pager/Pager';
import SearchBar from '../SearchBar';
import Spinner from '../Spinner';
import UsageAlerts from '../UsageAlerts';

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, startTs, endTs) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/campaignStats/${encodeURIComponent(
      startTs
    )}/${encodeURIComponent(endTs)}`
  );
}, 300);

const fetchDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, filter) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/campaigns?${queryString.stringify({ ...filter })}`
  );
}, 300);

const initialPeriodDays = 30;

const CampaignsPageCommon = ({ Cards, type }) => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const { accountId, domainId } = useParams();
  const { domain } = useSelector((state) => state.domain);

  const search = queryString.parse(location.search);
  const mode = search.mode;
  const isOneOff = mode === 'oneOff';

  const selectedPageSize = parseInt(search.resultsPerPage) || PageSizeOptions[0];
  const selectedPage = parseInt(search.page) || 1;
  const searchValue = search.searchTerms || '';

  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [campaigns, setCampaigns] = useState();
  const [stats, setStats] = useState();
  const [rescheduleStatuses, setRescheduleStatuses] = useState();
  const [isClickTrackingVerified, setIsClickTrackingVerified] = useState();
  const [error, setError] = useState();
  const [pageCount, setPageCount] = useState();
  const [loading, setLoading] = useState(false);

  // The list and the stats cover the same window, so both read these.
  const startTs = dateToStartOfDay(startDate, 'local');
  const endTs = dateToEndOfDay(endDate, 'local');
  // Only oneOff lists are filtered by the period; keeping these constant in the
  // other modes stops a period change from refetching a list that ignores it.
  const sentFrom = isOneOff ? startTs : undefined;
  const sentTo = isOneOff ? endTs : undefined;

  useEffect(() => {
    setLoading(true);

    const filterDefaultValues = { page: 1, resultsPerPage: selectedPageSize, type: type, searchTerms: '' };
    const filter = { ...filterDefaultValues, ...queryString.parse(location.search) };
    if (sentFrom) {
      filter.sentFrom = sentFrom;
      filter.sentTo = sentTo;
    }

    fetchDebounced(fetch, accountId, domainId, filter)
      .then(({ campaigns, pagesCount }) => {
        setCampaigns(campaigns);
        setPageCount(pagesCount);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [domainId, accountId, fetch, location.search, type, selectedPageSize, sentFrom, sentTo]);

  useEffect(() => {
    setStats(undefined);
    statsDebounced(fetch, accountId, domainId, startTs, endTs)
      .then((stats) => {
        setStats(stats);
      })
      .catch((error) => {
        setError(error);
      });
  }, [accountId, domainId, startTs, endTs, fetch]);

  useEffect(() => {
    if (!campaigns) {
      return;
    }
    // Only running one-offs that can still have unsent messages are worth asking
    // about - they are the ones daily-limit reschedules can hold up.
    const now = new Date();
    const runningOneOffCampaignIds = campaigns
      .filter(
        (campaign) =>
          campaign.mode === 'oneOff' &&
          campaign.startedAt &&
          !campaign.abortedAt &&
          (!campaign.finishedAt || new Date(campaign.finishedAt) > now)
      )
      .map((campaign) => campaign.id);
    if (runningOneOffCampaignIds.length === 0) {
      setRescheduleStatuses(undefined);
      return;
    }
    let cancelled = false;
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/rescheduleStatus`, {
      method: 'POST',
      body: JSON.stringify({ campaignIds: runningOneOffCampaignIds }),
    })
      .then(({ statuses }) => {
        // A response for a page the user already left must not clobber the current one.
        if (!cancelled) {
          setRescheduleStatuses(statuses);
        }
      })
      // A missing reschedule hint must not take down the whole list page.
      .catch(() => {
        if (!cancelled) {
          setRescheduleStatuses(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [campaigns, accountId, domainId, fetch]);

  // Fetched once for the whole list rather than per row: every campaign on the page
  // shares the domain's click-tracking state, which decides whether activation is
  // offered at all.
  useEffect(() => {
    let cancelled = false;
    setIsClickTrackingVerified(undefined);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/verificationStatus`)
      .then((verificationStatus) => {
        // A response for a domain the user already left must not clobber the current one.
        if (!cancelled) {
          setIsClickTrackingVerified(clickTrackingVerified(verificationStatus));
        }
      })
      // A DNS-status lookup that fails must not take the list page down, nor block
      // activation on a state we could not read.
      .catch(() => {
        if (!cancelled) {
          setIsClickTrackingVerified(undefined);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, domainId, fetch]);

  const setPageSize = async (pageSize) => {
    navigate({
      search: queryString.stringify({ ...queryString.parse(location.search), page: 1, resultsPerPage: pageSize }),
    });
  };

  const toggleCampaign = (campaignIndex, running) => {
    const campaign = campaigns[campaignIndex];
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}`, {
      method: 'PUT',
      body: JSON.stringify({ running, aborted: !running && campaign.mode === 'oneOff' }),
    })
      .then(({ campaign }) => {
        const updatedCampaigns = [...campaigns];
        updatedCampaigns[campaignIndex] = campaign;
        setCampaigns(updatedCampaigns);
      })
      .catch((e) => setError(e));
  };

  const setPeriod = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    // In oneOff mode the period narrows the list itself, so the page the user
    // was on no longer exists in the new result set.
    if (isOneOff) {
      navigate(
        { search: queryString.stringify({ ...queryString.parse(location.search), page: 1 }) },
        { replace: true }
      );
    }
  };

  const onSearchChange = (e) => {
    const newFilter = { ...queryString.parse(location.search), searchTerms: e.target.value.toLowerCase(), page: 1 };
    navigate({ search: queryString.stringify({ ...newFilter }) });
  };

  const handlePageClick = (e) => {
    const currentPage = e.selected;
    navigate({ search: queryString.stringify({ ...queryString.parse(location.search), page: currentPage }) });
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const metricsSelector = (
    <MetricsSelector
      startDate={startDate}
      endDate={endDate}
      setDateRange={setPeriod}
      rowSize="12"
      scope={'campaigns'}
    />
  );

  if (!campaigns) {
    return (
      <>
        {metricsSelector}
        <Spinner />
      </>
    );
  }

  const campaignElements = (
    <Cards
      campaigns={campaigns}
      stats={stats}
      period={{ startDate, endDate }}
      toggleCampaign={toggleCampaign}
      url={location.pathname}
      rescheduleStatuses={rescheduleStatuses}
      isClickTrackingVerified={isClickTrackingVerified}
    />
  );

  const restrictionElement = type === 'simple' ? 'campaign' : type;
  const restrictionKey = `${mode}${restrictionElement[0].toUpperCase() + restrictionElement.slice(1)}sCreate`;

  return (
    <div data-tour="workFlowsOverview">
      <Metadata
        title={`${type === 'workflow' ? `${Page['workflows']}` : `${Page['campaigns']}`} | ${CampaignTypes[mode]}`}
      />
      <UsageAlerts />
      <Group mb="md" align="flex-end" wrap="wrap" gap="md">
        {metricsSelector}
        {!isOneOff && (
          <PageFilter
            placeholder="On / Off"
            queryKey="running"
            options={[
              { value: 'true', label: 'On' },
              { value: 'false', label: 'Off' },
            ]}
          />
        )}
        <Box style={{ flex: 1, minWidth: 150 }}>
          <SearchBar onChange={onSearchChange} value={searchValue} />
        </Box>
        {mode !== 'custom' && (
          <NewElementButton
            onClick={() =>
              navigate({
                ...location,
                pathname: location.pathname + '/new',
                search: queryString.stringify({
                  ...search,
                  editor: type === 'simple' ? 'stripo' : undefined,
                }),
              })
            }
            title={`${type === 'workflow' ? `New Workflow` : `New Campaign`}`}
            restrictionKey={restrictionKey}
          />
        )}
      </Group>

      <SenderVerificationAlert />
      <EmailSendingHoldAlert accountId={domain.accountId} domainId={domain.id} />

      {loading ? (
        <Spinner />
      ) : campaigns.length === 0 ? (
        <Center py={80}>
          <Stack align="center" gap="md">
            <IconSearch size={48} stroke={1} color="var(--mantine-color-dimmed)" />
            <Stack align="center" gap="xs">
              <Title order={4}>{isOneOff ? 'No one-offs ran in the selected period' : 'No results found'}</Title>
              {isOneOff && <Text c="dimmed">Try a different period{searchValue ? ' or search term' : ''}.</Text>}
              {!isOneOff && searchValue && <Text c="dimmed">Try a different search term.</Text>}
            </Stack>
          </Stack>
        </Center>
      ) : (
        <>
          {campaignElements}
          <Pager
            pageSize={selectedPageSize}
            pageCount={pageCount}
            onPageChange={handlePageClick}
            initialPage={selectedPage}
            setPageSize={setPageSize}
          />
        </>
      )}
    </div>
  );
};

export default CampaignsPageCommon;
